const OFFER_PREVIEW_STORAGE_KEY = "ibragOfferPreviewByIdV1";
const CART_LINE_SELECTOR = "tr.cart-items__table-row[data-key]";
const CART_IMAGE_SELECTOR = "td.cart-items__media img.cart-items__media-image";

let isApplying = false;
let pendingTimer = null;

function safeJsonParse(value, fallback) {
  try {
    const parsed = JSON.parse(value);
    return parsed == null ? fallback : parsed;
  } catch (error) {
    return fallback;
  }
}

function readOfferPreviewStorage() {
  let raw = "";
  try {
    raw = window.localStorage.getItem(OFFER_PREVIEW_STORAGE_KEY) || "";
  } catch (error) {
    return {};
  }

  if (!raw) {
    return {};
  }

  const parsed = safeJsonParse(raw, {});
  return parsed && typeof parsed === "object" ? parsed : {};
}

function getOfferIdFromAttribution(attributionValue) {
  if (!attributionValue) {
    return "";
  }

  if (typeof attributionValue === "object") {
    return String(
      attributionValue.offer_id
      || attributionValue.offerId
      || attributionValue.offerID
      || ""
    ).trim();
  }

  if (typeof attributionValue !== "string") {
    return "";
  }

  const trimmed = attributionValue.trim();
  if (!trimmed) {
    return "";
  }

  const parsed = safeJsonParse(trimmed, null);
  if (parsed && typeof parsed === "object") {
    return String(parsed.offer_id || parsed.offerId || parsed.offerID || "").trim();
  }

  return "";
}

function getOfferPreviewUrl(storedByOfferId, offerId) {
  if (!offerId) {
    return "";
  }

  const entry = storedByOfferId?.[offerId];
  if (!entry) {
    return "";
  }

  if (typeof entry === "string") {
    return entry.trim();
  }

  if (typeof entry === "object") {
    return String(entry.previewImageUrl || entry.preview_image_url || entry.url || "").trim();
  }

  return "";
}

async function fetchCart() {
  const response = await fetch("/cart.js", {
    method: "GET",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load cart (${response.status}).`);
  }

  return response.json();
}

function setImageSource(img, nextImageUrl) {
  if (!img || !nextImageUrl) {
    return;
  }

  if (img.dataset.offerPreviewApplied === "true" && img.dataset.offerPreviewUrl === nextImageUrl) {
    return;
  }

  img.src = nextImageUrl;
  img.srcset = `${nextImageUrl} 250w`;
  img.dataset.offerPreviewApplied = "true";
  img.dataset.offerPreviewUrl = nextImageUrl;
}

async function applyOfferPreviewImages() {
  if (isApplying) {
    return;
  }

  const rows = Array.from(document.querySelectorAll(CART_LINE_SELECTOR));
  if (!rows.length) {
    return;
  }

  isApplying = true;

  try {
    const [cart, storedByOfferId] = await Promise.all([
      fetchCart(),
      Promise.resolve(readOfferPreviewStorage()),
    ]);

    const offerIdByCartKey = new Map();

    for (const lineItem of cart?.items || []) {
      const attribution = lineItem?.properties?._attribution;
      const directOfferId = String(
        lineItem?.properties?.offer_id
        || lineItem?.properties?.offerId
        || ""
      ).trim();
      const offerId = directOfferId || getOfferIdFromAttribution(attribution);

      if (lineItem?.key && offerId) {
        offerIdByCartKey.set(lineItem.key, offerId);
      }
    }

    for (const row of rows) {
      const cartKey = String(row.dataset.key || "").trim();
      if (!cartKey) {
        continue;
      }

      const offerId = offerIdByCartKey.get(cartKey);
      if (!offerId) {
        continue;
      }

      const previewImageUrl = getOfferPreviewUrl(storedByOfferId, offerId);
      if (!previewImageUrl) {
        continue;
      }

      const image = row.querySelector(CART_IMAGE_SELECTOR);
      setImageSource(image, previewImageUrl);
    }
  } catch (error) {
    // noop: leave default Shopify cart images
  } finally {
    isApplying = false;
  }
}

function scheduleApply(delayMs = 120) {
  window.clearTimeout(pendingTimer);
  pendingTimer = window.setTimeout(() => {
    applyOfferPreviewImages();
  }, delayMs);
}

function startOfferPreviewImageOverride() {
  scheduleApply(0);

  document.addEventListener("cart:update", () => {
    scheduleApply(180);
  });

  document.addEventListener("cart:error", () => {
    scheduleApply(180);
  });

  const observer = new MutationObserver((mutations) => {
    if (!mutations?.length) {
      return;
    }

    for (const mutation of mutations) {
      if (mutation.type !== "childList" || !mutation.addedNodes.length) {
        continue;
      }

      scheduleApply(180);
      break;
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", startOfferPreviewImageOverride, { once: true });
} else {
  startOfferPreviewImageOverride();
}
